import type { AxiosResponse } from 'axios'
import { Converter } from 'helper/Converter'
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from 'helper/constants'
import httpService from '../httpService'
import { MENTOR_ROLES } from '../pages/batches/constants'
import type { ConversationInfo } from 'pages/batches/components/ConversationDetail'
import { PackUpdateStatus } from '../pages/batches/types'
import { Filter } from '../redux/students/types'
import qs from 'qs'

/**
 * Returns list of batches
 */
export function listBatches(filters: any) {
  return httpService.get('/v9/batches/', {
    params: filters,
  })
}

/**
 * Returns batch details
 */
export function getBatch(batchId: string) {
  return httpService.get(`/v9/batches/${batchId}/`)
}

/**
 * Returns users (mentors and coordinators)
 */
export function getUsers(username: string) {
  return httpService.get('/v4/content/sources/users/', {
    params: {
      username,
      role: MENTOR_ROLES,
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' })
    },
  })
}

/**
 * Create new batch
 */
export function createBatch(data: any) {
  return httpService.post(`/v9/batches/`, data)
}

/**
 * Update batch details
 */
export function updateBatch(batchId: string, data: any) {
  return httpService.put(`/v9/batches/${batchId}/`, data)
}

/**
 * Archive/Unarchive batch
 */
export function updateBatchStatus(batchId: string, action: 'UNARCHIVE' | 'ARCHIVE') {
  return httpService.patch(`/v9/batches/${batchId}/`, { isArchived: action === 'ARCHIVE' })
}

/**
 * close batch
 */
export function closeBatchStatus(batchId: string) {
  return httpService.patch(`/v9/batches/${batchId}/`, { isCompleted: true })
}

/**
 * Add students to batch
 */
export function addStudents(batchId: string, data: Object) {
  return httpService.post(`/v9/batches/${batchId}/users/add/`, data)
}

/**
 * Get students list
 */
export function getStudents(
  batchId: string,
  filters?: Filter & { search?: string; exclude_conversation_id?: string },
) {
  return httpService.get(`/v9/batches/${batchId}/users/`, {
    params: filters,
  })
}

/**
 * Remove student from batch
 */
export function removeStudent(batchId: number | string, userId: number | string) {
  return httpService.delete(`/v9/batches/${batchId}/users/${userId}/`)
}

/**
 * Map pack to batch
 */
export function addPack(batchId: number | string, data: any) {
  return httpService.post(`/v9/batches/${batchId}/packs/`, data)
}

/**
 * Returns packs of batch
 */
export function fetchPacks(batchId: number | string) {
  return httpService.get(`/v9/batches/${batchId}/packs/`)
}

/**
 * Update pack status
 */
export function updatePackStatus(data: PackUpdateStatus) {
  return httpService.patch(`v9/batches/${data.batchId}/packs/${data.packId}/`, data.updatedValues)
}

/**
 * Update entri staff flag in batch participants
 */
export function updateEntriStaffStatus(id: number, isEntriEmployee: boolean) {
  return httpService.post(`v9/users/${id}/set-entri-employee/`, { isEntriEmployee })
}

/**
 * Returns conversations mapped to a batch (via the generic endpoint).
 */
export function fetchBatchConversations(
  batchId: number | string,
  params: {
    page?: number
    pageSize?: number
    type?: 'group' | 'dm' | 'managed_dm'
    includeRemoved?: boolean
  } = {},
) {
  const { page = 1, pageSize = 20, type, includeRemoved = true } = params
  return httpService
    .get('/v9.1/dashboard/chat/conversations/', {
      params: {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        type,
        batch_uid: batchId,
        include_removed: includeRemoved,
      },
    })
    .then(res => ({ ...res, data: Converter.toCamelCase(res.data) }))
}

/**
 * Create a conversation mapped to a batch (via the generic endpoint).
 */
export function createBatchConversation(
  batchId: number | string,
  data: {
    type: 'dm' | 'group' | 'managed_dm'
    userIds?: number[]
    allBatchMembers?: boolean
    name?: string
    description?: string
    isAdminOnly?: boolean
    isMandatory?: boolean
  },
) {
  return httpService.post(
    '/v9.1/dashboard/chat/conversations/',
    Converter.toSnakeCase({ ...data, batchUids: [batchId] }),
  )
}

/**
 * Get conversation details with a page of members.
 * `pageSize` is sent as `page_size` and the response is converted to camelCase.
 * `members` holds only the requested page; `totalCount` (from `total_count`) counts every
 * member of the conversation. A backend without member paging ignores both params and returns
 * every member, possibly without `total_count`, so callers fall back to `members.length`.
 */
export function getConversationDetail(
  conversationId: string,
  params: { page?: number; pageSize?: number } = {},
): Promise<AxiosResponse<ConversationInfo>> {
  const { page = FIRST_PAGE, pageSize = DEFAULT_PAGE_SIZE } = params
  return httpService
    .get(`/v9.1/dashboard/chat/conversations/${conversationId}`, {
      params: { page, page_size: pageSize },
    })
    .then(res => ({ ...res, data: Converter.toCamelCase(res.data) as ConversationInfo }))
}

/**
 * Add members to a conversation
 */
export function addConversationMembers(
  conversationId: string,
  userIds: number[],
  batchId?: number,
) {
  return httpService.post(`/v9.1/dashboard/chat/conversations/${conversationId}/members`, {
    user_ids: userIds,
    ...(batchId && { batch_id: batchId }),
  })
}

/**
 * Remove a member from a conversation
 */
export function removeConversationMember(conversationId: string, memberUId: string) {
  return httpService.delete(
    `/v9.1/dashboard/chat/conversations/${conversationId}/members/${memberUId}`,
  )
}

/**
 * Update a member's role in a conversation
 */
export function updateMemberRole(
  conversationId: string,
  memberUId: string,
  role: 'admin' | 'member',
) {
  return httpService.patch(
    `/v9.1/dashboard/chat/conversations/${conversationId}/members/${memberUId}/role`,
    { role },
  )
}

/**
 * Bulk create DMs between batch mentor and all members
 */
export function bulkCreateBatchDMs(batchId: number | string) {
  return httpService
    .post(`/v9.1/dashboard/chat/batches/${batchId}/bulk-dm/`)
    .then(res => ({ ...res, data: Converter.toCamelCase(res.data) }))
}

/**
 * Update conversation name, description, isAdminOnly and/or isMandatory
 */
export function updateConversation(
  conversationId: string,
  data: {
    name?: string
    description?: string
    isAdminOnly?: boolean
    isMandatory?: boolean
    isPublicJoinEnabled?: boolean
  },
) {
  return httpService.patch(
    `/v9.1/dashboard/chat/conversations/${conversationId}`,
    Converter.toSnakeCase(data),
  )
}

const PACK_SEARCH_PAGE_SIZE = 20

/**
 * Search packs by name for the batch filter course dropdown
 */
export function searchPacks(name: string) {
  return httpService.get('/v9.1/packs/', {
    params: { name, page_size: PACK_SEARCH_PAGE_SIZE },
  })
}

/**
 * Look up a single pack by id, to resolve the course filter's display label
 * when a course_pack_id is restored (e.g. from the URL) without the user searching
 */
export function getPackById(id: number) {
  return httpService.get('/v9.1/packs/', {
    params: { ids: id },
  })
}
